import {
	createContext,
	type FC,
	type PropsWithChildren,
	useCallback,
	useContext,
	useMemo,
	useState,
} from "react";
import {
	getAllowedObjectivesForProduct,
	getDefaultGoalForObjective,
	TIKTOK_ADS_MIN_DAILY_BUDGET,
	TIKTOK_ADS_TOPVIEW_MIN_DAILY_BUDGET,
	TikTokAdsBudgetMode,
	type TikTokAdsCampaignDraft,
	TikTokAdsGender,
	TikTokAdsObjective,
	TikTokAdsPlacementType,
	TikTokAdsProductType,
} from "shared";
import type { StepSettings } from "../components/Stepper";

const BASE_WIZARD_STEPS: StepSettings[] = [
	{ title: "Campaign", icon: "campaign" },
	{ title: "Ad Group", icon: "settings" },
	{ title: "Creative", icon: "photo_library" },
	{ title: "Review", icon: "fact_check" },
];

const LEAD_FORM_STEP: StepSettings = {
	title: "Lead Form",
	icon: "description",
};

const wizardStepsForObjective = (
	objective: TikTokAdsObjective,
): StepSettings[] => {
	if (objective === TikTokAdsObjective.LEAD_GENERATION) {
		return [
			BASE_WIZARD_STEPS[0],
			BASE_WIZARD_STEPS[1],
			LEAD_FORM_STEP,
			BASE_WIZARD_STEPS[2],
			BASE_WIZARD_STEPS[3],
		].map((step) => ({ ...step }));
	}
	return BASE_WIZARD_STEPS.map((step) => ({ ...step }));
};

const OBJECTIVE_LABELS: Record<TikTokAdsObjective, string> = {
	[TikTokAdsObjective.REACH]: "Reach",
	[TikTokAdsObjective.TRAFFIC]: "Traffic",
	[TikTokAdsObjective.VIDEO_VIEWS]: "Video Views",
	[TikTokAdsObjective.ENGAGEMENT]: "Engagement",
	[TikTokAdsObjective.LEAD_GENERATION]: "Leads",
	[TikTokAdsObjective.WEB_CONVERSIONS]: "Conversions",
};

export const objectiveLabel = (objective: TikTokAdsObjective): string =>
	OBJECTIVE_LABELS[objective];

const applyObjectiveDefaults = (
	draft: TikTokAdsCampaignDraft,
	objective: TikTokAdsObjective,
): TikTokAdsCampaignDraft => {
	const defaults = getDefaultGoalForObjective(objective);
	return {
		...draft,
		objective,
		optimizationGoal: defaults.goal,
		billingEvent: defaults.billing,
		bidPrice: defaults.requiresBid ? defaults.defaultBid : null,
	};
};

const buildDefaultName = (
	companyName: string,
	objective: TikTokAdsObjective,
	productType: TikTokAdsProductType,
): string => {
	const date = new Date().toISOString().split("T")[0];
	const product =
		productType === TikTokAdsProductType.TOPVIEW ? "TopView" : "In-Feed";
	return `${companyName} – ${product} – ${OBJECTIVE_LABELS[objective]} – ${date}`;
};

function createEmptyDraft(): TikTokAdsCampaignDraft {
	const objective = TikTokAdsObjective.TRAFFIC;
	const defaults = getDefaultGoalForObjective(objective);

	return {
		id: crypto.randomUUID(),
		name: "",
		productType: TikTokAdsProductType.IN_FEED,
		objective,
		budgetMode: TikTokAdsBudgetMode.DAILY,
		budgetAmount: TIKTOK_ADS_MIN_DAILY_BUDGET,
		adGroupName: "",
		scheduleStart: formatSchedule(new Date()),
		scheduleEnd: null,
		optimizationGoal: defaults.goal,
		billingEvent: defaults.billing,
		bidPrice: defaults.requiresBid ? defaults.defaultBid : null,
		placementType: TikTokAdsPlacementType.AUTOMATIC,
		placements: ["PLACEMENT_TIKTOK"],
		locationIds: [],
		ageGroups: ["AGE_18_24", "AGE_25_34", "AGE_35_44"],
		gender: TikTokAdsGender.UNLIMITED,
		languages: [],
		identityId: "",
		identityType: "CUSTOMIZED_USER",
		identityAuthorizedBcId: null,
		mediaFileId: null,
		adName: "",
		adText: "",
		callToAction: "LEARN_MORE",
		landingPageUrl: "",
		displayName: "",
		instantFormPageId: null,
		leadForm: null,
		pixelId: null,
		optimizationEvent: null,
		createdAt: "",
		updatedAt: "",
	};
}

/** TikTok expects `YYYY-MM-DD HH:mm:ss` in the advertiser's timezone. */
export function formatSchedule(date: Date): string {
	const pad = (value: number) => String(value).padStart(2, "0");
	return (
		`${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
		` ${pad(date.getHours())}:${pad(date.getMinutes())}:00`
	);
}

/** `YYYY-MM-DD HH:mm:ss` → the `YYYY-MM-DDTHH:mm` a datetime-local input wants. */
export const scheduleToInputValue = (value: string): string =>
	value ? value.replace(" ", "T").slice(0, 16) : "";

export const inputValueToSchedule = (value: string): string =>
	value ? `${value.replace("T", " ")}:00` : "";

type TikTokCampaignWizardContextType = {
	draft: TikTokAdsCampaignDraft;
	updateDraft: (partial: Partial<TikTokAdsCampaignDraft>) => void;
	isDirty: boolean;
	currentStep: number;
	steps: StepSettings[];
	nextStep: () => void;
	prevStep: () => void;
};

const TikTokCampaignWizardContext =
	createContext<TikTokCampaignWizardContextType | null>(null);

interface ProviderProps extends PropsWithChildren {
	initialOverrides?: Partial<TikTokAdsCampaignDraft>;
	companyName?: string;
}

export const TikTokCampaignWizardProvider: FC<ProviderProps> = ({
	children,
	initialOverrides,
	companyName = "Campaign",
}) => {
	const [isDirty, setIsDirty] = useState(false);
	const [draft, setDraft] = useState<TikTokAdsCampaignDraft>(() => {
		const objective = initialOverrides?.objective ?? TikTokAdsObjective.TRAFFIC;
		const productType =
			initialOverrides?.productType ?? TikTokAdsProductType.IN_FEED;
		return {
			...createEmptyDraft(),
			name: buildDefaultName(companyName, objective, productType),
			...initialOverrides,
		};
	});
	const [currentStep, setCurrentStep] = useState(0);
	const [steps, setSteps] = useState<StepSettings[]>(() =>
		wizardStepsForObjective(
			initialOverrides?.objective ?? TikTokAdsObjective.TRAFFIC,
		),
	);

	const updateDraft = useCallback(
		(partial: Partial<TikTokAdsCampaignDraft>) => {
			setIsDirty(true);
			setDraft((prev) => {
				let next = { ...prev, ...partial };

				if (partial.productType && partial.productType !== prev.productType) {
					const allowed = getAllowedObjectivesForProduct(partial.productType);
					if (!allowed.includes(next.objective)) {
						next = applyObjectiveDefaults(next, allowed[0]);
					}
					if (partial.productType === TikTokAdsProductType.TOPVIEW) {
						next.budgetAmount = Math.max(
							next.budgetAmount,
							TIKTOK_ADS_TOPVIEW_MIN_DAILY_BUDGET,
						);
						next.placementType = TikTokAdsPlacementType.MANUAL;
						next.placements = ["PLACEMENT_TIKTOK"];
					}
					if (!partial.name) {
						next.name = buildDefaultName(
							companyName,
							next.objective,
							partial.productType,
						);
					}
				}

				if (partial.objective && partial.objective !== prev.objective) {
					next = applyObjectiveDefaults(next, partial.objective);
					if (partial.objective !== TikTokAdsObjective.LEAD_GENERATION) {
						next.leadForm = null;
						next.instantFormPageId = null;
					}
					if (partial.objective !== TikTokAdsObjective.WEB_CONVERSIONS) {
						next.pixelId = null;
						next.optimizationEvent = null;
					} else if (!next.optimizationEvent) {
						next.optimizationEvent = "COMPLETE_PAYMENT";
					}
					if (
						partial.objective === TikTokAdsObjective.LEAD_GENERATION ||
						partial.objective === TikTokAdsObjective.VIDEO_VIEWS
					) {
						// These objectives reject PLACEMENT_TYPE_AUTOMATIC (40002).
						next.placementType = TikTokAdsPlacementType.MANUAL;
						next.placements =
							partial.objective === TikTokAdsObjective.LEAD_GENERATION
								? ["PLACEMENT_TIKTOK"]
								: next.placements.length > 0
									? next.placements
									: ["PLACEMENT_TIKTOK"];
					}
					if (!partial.name) {
						next.name = buildDefaultName(
							companyName,
							partial.objective,
							next.productType,
						);
					}
				}

				return next;
			});

			if (partial.objective) {
				setSteps(wizardStepsForObjective(partial.objective));
				setCurrentStep(0);
			}
		},
		[companyName],
	);

	const nextStep = useCallback(() => {
		setSteps((prev) =>
			prev.map((step, i) =>
				i === currentStep ? { ...step, complete: true } : step,
			),
		);
		setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
	}, [currentStep, steps.length]);

	const prevStep = useCallback(() => {
		setCurrentStep((prev) => Math.max(prev - 1, 0));
	}, []);

	const value = useMemo(
		() => ({
			draft,
			updateDraft,
			isDirty,
			currentStep,
			steps,
			nextStep,
			prevStep,
		}),
		[draft, updateDraft, isDirty, currentStep, steps, nextStep, prevStep],
	);

	return (
		<TikTokCampaignWizardContext.Provider value={value}>
			{children}
		</TikTokCampaignWizardContext.Provider>
	);
};

export const useTikTokCampaignWizard = () => {
	const context = useContext(TikTokCampaignWizardContext);
	if (!context) {
		throw new Error(
			"useTikTokCampaignWizard must be used within a TikTokCampaignWizardProvider",
		);
	}
	return context;
};
